import React from 'react';
import { Box, Chip, Paper, Skeleton, Stack, Tooltip, Typography, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import PersonRounded from '@mui/icons-material/PersonRounded';

/**
 * Shared pieces for the four Sales Operations screens.
 *
 * Layout and copy follow `docs/features/sales-operations/mockups/rocco.html`,
 * but the colours and type come from the platform theme rather than the
 * mockup's own palette -- handover 2.1 requires this to read as a surface
 * *within* Klaris, and a navy/teal island would not.
 *
 * FORMATTING RULE: handover 3.5 says round every displayed number. These
 * helpers are the single place that happens, so "$1,316" cannot appear on one
 * screen and "$1,315.87" on another.
 */

/** Percentage from a fraction. `pct(0.162)` -> "16.2%". */
export function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return `${(value * 100).toFixed(digits)}%`;
}

/** Whole dollars. Money on these screens is never shown to the cent. */
export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return `$${Math.round(value).toLocaleString()}`;
}

/** Large money, abbreviated: 164_000 -> "$164k". */
export function moneyShort(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${Math.round(value)}`;
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return Math.round(value).toLocaleString();
}

export function num(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return value.toFixed(digits);
}

/**
 * A headline tile. `tone` colours the value only -- never the whole card,
 * which would turn a dashboard into a traffic-light display and bury the
 * numbers that are merely normal.
 */
export function Tile({
  label,
  value,
  detail,
  tone = 'neutral',
  tooltip,
  loading,
}: {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  tone?: 'neutral' | 'good' | 'warn';
  tooltip?: string;
  loading?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        px: 2.25,
        py: 2,
        borderRadius: 2,
        borderColor: t.tokens?.border?.subtle ?? t.palette.divider,
        bgcolor: t.tokens?.surface?.raised ?? t.palette.background.paper,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          sx={{
            fontSize: 11.5,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <InfoOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Stack>
      {loading ? (
        <>
          <Shimmer width="55%" height={35} sx={{ mt: 0.75 }} />
          <Shimmer width="75%" height={13} sx={{ mt: 0.75 }} />
        </>
      ) : (
        <>
          <Typography
            sx={(t) => ({
              fontSize: 32,
              fontWeight: 600,
              letterSpacing: '-0.025em',
              mt: 0.75,
              lineHeight: 1.1,
              color:
                tone === 'good'
                  ? t.palette.success.main
                  : tone === 'warn'
                    ? t.palette.warning.main
                    : t.palette.text.primary,
            })}
          >
            {value}
          </Typography>
          {detail && (
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>{detail}</Typography>
          )}
        </>
      )}
    </Paper>
  );
}

/** A bordered section with an uppercase rule-line header, as in the mockup. */
export function Card({
  title,
  children,
  action,
  dense,
}: {
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  dense?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        borderRadius: 2,
        borderColor: t.tokens?.border?.subtle ?? t.palette.divider,
        bgcolor: t.tokens?.surface?.raised ?? t.palette.background.paper,
        overflow: 'hidden',
      })}
    >
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={(t) => ({
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
          })}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: 'text.secondary',
            }}
          >
            {title}
          </Typography>
          {action}
        </Stack>
      )}
      <Box sx={{ p: dense ? 0 : 2 }}>{children}</Box>
    </Paper>
  );
}

/**
 * Loading placeholders.
 *
 * Per-component rather than one spinner for the whole screen: the page's
 * structure is known before its data is, so showing it immediately means the
 * layout does not jump when the numbers land, and a slow section does not
 * hold the rest of the page hostage. Queue runs four warehouse queries and is
 * the slowest of the four screens — a blank page for that long reads as
 * broken.
 *
 * `wave` rather than `pulse` because it reads as "this is coming" instead of
 * "this is disabled".
 */
export function Shimmer({
  width,
  height,
  sx,
}: {
  width?: number | string;
  height?: number | string;
  sx?: SxProps<Theme>;
}) {
  return (
    <Skeleton
      variant="rounded"
      animation="wave"
      width={width}
      height={height}
      sx={[{ borderRadius: 1 }, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
    />
  );
}

/** Placeholder rows shaped like the table that will replace them. */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {Array.from({ length: rows }).map((_, r) => (
        <Stack
          key={r}
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ py: 1.25 }}
        >
          {Array.from({ length: cols }).map((__, c) => (
            <Shimmer
              key={c}
              height={13}
              // First column is a name and reads wider; the rest are figures.
              width={c === 0 ? '26%' : `${Math.max(8, 46 / cols)}%`}
            />
          ))}
        </Stack>
      ))}
    </Box>
  );
}

/** Placeholder for the label-and-bar lists (trend, capacity). */
export function BarListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <Stack spacing={1.5}>
      {Array.from({ length: rows }).map((_, i) => (
        <Box key={i}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Shimmer width={92} height={12} />
            <Shimmer width={54} height={12} />
          </Stack>
          <Shimmer width="100%" height={8} />
        </Box>
      ))}
    </Stack>
  );
}

/** Who is taking a lead. AI reads visually distinct from a person on purpose. */
export function AssigneeChip({ name, type }: { name: string | null; type: string | null }) {
  const isAi = type === 'AI';
  return (
    <Chip
      size="small"
      icon={isAi ? <SmartToyOutlined sx={{ fontSize: 14 }} /> : <PersonRounded sx={{ fontSize: 14 }} />}
      label={name ?? '–'}
      sx={(t) => ({
        height: 22,
        fontSize: 11.5,
        fontWeight: 500,
        bgcolor: alpha(isAi ? t.palette.secondary.main : t.palette.primary.main, 0.12),
        color: isAi ? t.palette.secondary.main : t.palette.primary.main,
        '& .MuiChip-icon': { color: 'inherit', ml: 0.75 },
      })}
    />
  );
}

/**
 * A number that rests on too little evidence to state flatly.
 *
 * Handover 3.5: a human must be able to see where a number came from. That
 * includes seeing when there isn't enough behind it -- a margin computed on
 * eleven policies is not the same object as one computed on four hundred, and
 * rendering them identically is the quiet way a scorecard starts making
 * decisions it cannot support.
 */
export function ThinEvidence({ observed, minimum }: { observed: number | null; minimum: number }) {
  return (
    <Tooltip
      arrow
      title={
        `Computed on ${observed ?? 0} policies with an observed 12-month outcome, ` +
        `below the ${minimum} this product treats as enough to state a point ` +
        `estimate. Directionally useful, not a number to act on alone.`
      }
    >
      <Chip
        label="thin"
        size="small"
        sx={(t) => ({
          height: 18,
          fontSize: 10,
          ml: 0.75,
          bgcolor: alpha(t.palette.warning.main, 0.14),
          color: t.palette.warning.main,
        })}
      />
    </Tooltip>
  );
}

/**
 * A labelled progress bar: name on the left, value on the right, bar beneath.
 *
 * Shared by Today's contact-rate comparison and Plan's capacity view. `striped`
 * marks a figure that is a target rather than a measurement, so the two never
 * look like the same kind of number.
 */
export function StatBar({
  label,
  sublabel,
  value,
  display,
  max,
  tone = 'neutral',
  striped,
}: {
  label: string;
  sublabel?: string;
  value: number | null;
  display: string;
  max: number;
  tone?: 'neutral' | 'accent' | 'warn';
  striped?: boolean;
}) {
  const width = value !== null && max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {label}
          {sublabel && (
            <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', ml: 0.75 }}>
              {sublabel}
            </Typography>
          )}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: 13.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color:
              tone === 'accent' ? t.palette.primary.main
                : tone === 'warn' ? t.palette.warning.main
                  : t.palette.text.primary,
          })}
        >
          {display}
        </Typography>
      </Stack>
      <Box
        sx={(t) => ({
          height: 8,
          borderRadius: 1,
          bgcolor: t.tokens?.surface?.sunken ?? 'action.hover',
          overflow: 'hidden',
          mt: 0.75,
        })}
      >
        <Box
          sx={(t) => {
            const colour =
              tone === 'accent' ? t.palette.primary.main
                : tone === 'warn' ? t.palette.warning.main
                  : t.palette.text.disabled;
            return {
              height: '100%',
              width: `${width}%`,
              borderRadius: 1,
              ...(striped
                ? {
                    backgroundImage:
                      `repeating-linear-gradient(90deg, ${alpha(colour, 0.55)} 0 6px, transparent 6px 12px)`,
                  }
                : { bgcolor: colour }),
            };
          }}
        />
      </Box>
    </Box>
  );
}

/** The accent-tinted reasoning box on the decision panel. */
export function ReasoningBox({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={(t) => ({
        bgcolor: alpha(t.palette.primary.main, 0.07),
        borderLeft: `3px solid ${t.palette.primary.main}`,
        borderRadius: '0 6px 6px 0',
        px: 1.75,
        py: 1.5,
        fontSize: 13,
        lineHeight: 1.6,
        color: 'text.primary',
      })}
    >
      {children}
    </Box>
  );
}

/** A small caption used under tables and charts to state the method. */
export function MethodNote({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{ fontSize: 12, color: 'text.disabled', mt: 1.25, fontStyle: 'italic', lineHeight: 1.5 }}
    >
      {children}
    </Typography>
  );
}

export const tableSx = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  fontSize: 13,
  '& th': {
    textAlign: 'left' as const,
    fontWeight: 500,
    fontSize: 11.5,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'text.disabled',
    padding: '10px 16px',
    whiteSpace: 'nowrap' as const,
  },
  '& td': { padding: '14px 16px' },
  '& .num': { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' },
};
